// Copyright (c) 2026 Goffy. This file belongs to Goffy. All Rights Reserved.

import { Stack, useRouter } from 'expo-router';
import { useState, type ComponentType } from 'react';
import { Pressable, ScrollView, StyleSheet } from 'react-native';

import { Text, View } from '@/components/Themed';
import { useI18n } from '@/i18n';
import appColors from '@/theme/colors';
import {
  CanopyNudgeDiagram,
  DetectDiagram,
  FilterFunnelDiagram,
  JobFanOutDiagram,
  MergeDiagram,
  ModelStopsDiagram,
  PrecedenceDiagram,
  StageTimeDiagram,
  WindowGridDiagram,
} from '@/features/fleet/AnalysisHelpDiagrams';

/**
 * How a course analysis works, one step at a time.
 *
 * The fleet page shows runs and the steps panel shows what one run did; neither explains what
 * the stages ARE. That knowledge lived in three services' source comments, so "why did this
 * course get no bunkers" was a question only whoever wrote the pipeline could answer.
 *
 * Stepped rather than one long page on purpose: the pipeline is a sequence, and each step's
 * diagram only makes sense next to its own paragraph. Open to anyone who can reach the fleet
 * page — it explains the system rather than exposing any of its data.
 */

/** The steps, in the order the pipeline runs them. Keys are `analysisHelpPage.steps.<id>.*`. */
const STEPS: { id: string; Diagram: ComponentType | null }[] = [
  { id: 'request', Diagram: null },
  { id: 'dispatch', Diagram: JobFanOutDiagram },
  { id: 'jobs', Diagram: null },
  { id: 'imagery', Diagram: WindowGridDiagram },
  { id: 'detect', Diagram: DetectDiagram },
  { id: 'filter', Diagram: FilterFunnelDiagram },
  { id: 'merge', Diagram: MergeDiagram },
  { id: 'promote', Diagram: PrecedenceDiagram },
  { id: 'canopy', Diagram: CanopyNudgeDiagram },
  { id: 'model', Diagram: ModelStopsDiagram },
  { id: 'time', Diagram: StageTimeDiagram },
];

export default function AnalysisHelpScreen() {
  const { t } = useI18n();
  const [index, setIndex] = useState(0);

  const router = useRouter();
  const close = () => {
    // Opened from the fleet page this pops back to it; on a direct URL there is no
    // history to pop, so land on the start screen instead of doing nothing.
    if (router.canGoBack()) router.back();
    else router.replace('/');
  };

  const step = STEPS[index];
  const isFirst = index === 0;
  const isLast = index === STEPS.length - 1;

  return (
    <>
      <Stack.Screen options={{ title: t('analysisHelpPage.title') }} />
      <ScrollView contentContainerStyle={styles.container}>
        <Text style={styles.title}>{t('analysisHelpPage.title')}</Text>
        <Text style={styles.intro}>{t('analysisHelpPage.intro')}</Text>

        <View style={styles.dots}>
          {STEPS.map((candidate, dotIndex) => (
            <Pressable
              key={candidate.id}
              onPress={() => setIndex(dotIndex)}
              accessibilityRole="button"
              accessibilityLabel={t(`analysisHelpPage.steps.${candidate.id}.title`)}
              testID={`analysis-help-dot-${candidate.id}`}
              style={[styles.dot, dotIndex === index ? styles.dotActive : null]}
            />
          ))}
        </View>

        <View style={styles.card} testID="analysis-help-step">
          <Text style={styles.stepCount}>
            {t('analysisHelpPage.stepCount', { step: index + 1, total: STEPS.length })}
          </Text>
          <Text style={styles.stepTitle} testID="analysis-help-step-title">
            {t(`analysisHelpPage.steps.${step.id}.title`)}
          </Text>
          <Text style={styles.stepBody}>{t(`analysisHelpPage.steps.${step.id}.body`)}</Text>

          {step.Diagram ? (
            <View style={styles.diagram} testID="analysis-help-diagram">
              <step.Diagram />
            </View>
          ) : null}
        </View>

        <View style={styles.navRow}>
          <Pressable
            onPress={close}
            accessibilityRole="button"
            testID="analysis-help-close"
            style={styles.button}
          >
            <Text style={styles.buttonText}>{t('menu.close')}</Text>
          </Pressable>
          <View style={styles.stepButtons}>
            <Pressable
              onPress={() => setIndex((current) => Math.max(0, current - 1))}
              disabled={isFirst}
              accessibilityRole="button"
              testID="analysis-help-back"
              style={[styles.button, isFirst ? styles.buttonDisabled : null]}
            >
              <Text style={[styles.buttonText, isFirst ? styles.buttonTextDisabled : null]}>
                {t('analysisHelpPage.back')}
              </Text>
            </Pressable>
            <Pressable
              onPress={() => setIndex((current) => Math.min(STEPS.length - 1, current + 1))}
              disabled={isLast}
              accessibilityRole="button"
              testID="analysis-help-next"
              style={[styles.button, isLast ? styles.buttonDisabled : null]}
            >
              <Text style={[styles.buttonText, isLast ? styles.buttonTextDisabled : null]}>
                {t('analysisHelpPage.next')}
              </Text>
            </Pressable>
          </View>
        </View>
      </ScrollView>
    </>
  );
}

const styles = StyleSheet.create({
  container: { padding: 16, gap: 4 },
  title: { fontSize: 26, fontWeight: '700', color: appColors.text.primary },
  intro: { color: appColors.text.secondary, fontSize: 13, marginBottom: 12, lineHeight: 18 },
  dots: { flexDirection: 'row', gap: 6, marginBottom: 12, flexWrap: 'wrap' },
  dot: {
    width: 22,
    height: 4,
    borderRadius: 2,
    backgroundColor: appColors.border.default,
  },
  dotActive: { backgroundColor: appColors.accent.primary },
  card: {
    borderRadius: 12,
    borderWidth: StyleSheet.hairlineWidth,
    borderColor: appColors.border.default,
    padding: 14,
    gap: 8,
  },
  stepCount: { color: appColors.text.muted, fontSize: 11, letterSpacing: 0.5 },
  stepTitle: { color: appColors.text.primary, fontSize: 18, fontWeight: '700' },
  stepBody: { color: appColors.text.secondary, fontSize: 14, lineHeight: 20 },
  diagram: { marginTop: 8 },
  navRow: { flexDirection: 'row', justifyContent: 'space-between', gap: 12, marginTop: 14 },
  stepButtons: { flexDirection: 'row', gap: 12 },
  button: {
    paddingHorizontal: 16,
    paddingVertical: 8,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: appColors.accent.primary,
  },
  buttonDisabled: { borderColor: appColors.border.default },
  buttonText: { color: appColors.accent.primary, fontWeight: '700', fontSize: 13 },
  buttonTextDisabled: { color: appColors.text.muted },
});
